import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { leadService, quoteService, pricingService, integrationService } from '../services/api';
import { classifyMarginHealth, estimateConfidence, HEALTH_TONES } from '../config/costIntelligence';
import QuoteOperationalIntelligence from './QuoteOperationalIntelligence';
import {
  Calculator, Users, Loader2, ChevronRight, Plus, X, Search, RefreshCw, Lock,
  Send, Check, XCircle, Clock, Link as LinkIcon, ExternalLink, History,
  ShieldAlert, Sparkles, FileText, MessageSquare, Layers, Info, Edit3, GitBranch,
  DollarSign, Fuel, Truck, Wrench, EyeOff, TrendingUp, TrendingDown, ChevronDown,
} from 'lucide-react';

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d) => { try { return new Date(d).toLocaleString(); } catch { return d || '—'; } };
const nameOf = (a) => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email;

const QUOTE_STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-rose-100 text-rose-800',
  expired: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-gray-200 text-gray-600',
};

// ── Deterministic AI-assist (no external provider). Uses existing BLA-STD-007
// pricing-preview output + local completeness checks. Every item is a SUGGESTION
// requiring human review — never authoritative, never auto-applied. ────────────
const buildAssist = ({ customer, service, preview, discountResult, lineItems, exception }) => {
  const items = [];
  const add = (kind, text) => items.push({ kind, text });

  if (!service) {
    add('info', 'Select an active, quotable service to begin. Only Founder-approved active services can be placed on a live quote.');
    return items;
  }
  if (service.status !== 'active') {
    add('block', `${service.code} is "${service.status}" and is NOT quotable. Founder approval is required before it can appear on a live quote.`);
  } else {
    add('info', `Pricing method for ${service.code}: ${(service.pricing_method || 'n/a').replace(/_/g, ' ')}.`);
  }

  if (preview) {
    (preview.missing_inputs || []).forEach((m) => add('warn', `Missing input: "${m}". Enter it or ask the customer before quoting.`));
    if (preview.flags?.includes('below_minimum_charge')) {
      add('warn', `Amount is below the approved minimum charge (${money(preview.minimum_charge)}). The minimum has been applied.`);
    }
    if (preview.flags?.includes('below_margin_floor')) {
      add('block', 'This price falls below the approved contribution-margin floor. Founder approval / a governed exception is required.');
    } else if (preview.flags?.includes('below_target_margin')) {
      add('warn', 'This price is below the target margin but above the floor. Consider adjusting or note the rationale.');
    }
    if (preview.custom_quote_required) add('info', 'This service requires a custom quote — confirm scope with the customer.');
    if (preview.confidence === 'low') add('warn', 'Confidence is LOW because required figures are missing. Fill them in for a reliable projection.');
  }

  if (discountResult) {
    if (discountResult.flags?.includes('exceeds_routine_discount_limit')) {
      add('warn', `Discount exceeds the routine limit (${Math.round((discountResult.routine_limit || 0) * 100)}%). Approval is required.`);
    }
    if (discountResult.flags?.includes('violates_margin_floor')) {
      add('block', 'This discount would break the margin floor and is not allowed without a governed exception.');
    }
  }

  if (exception?.enabled) {
    if (!exception.explanation?.trim() || !exception.approving_authority?.trim()) {
      add('block', 'A pricing exception requires an explanation AND a recorded approving authority before it can be saved.');
    } else {
      add('info', 'A governed exception is attached — the standard price and the adjusted result will both be preserved for audit.');
    }
  }

  // Customer completeness
  if (customer) {
    if (!customer.email) add('warn', 'Customer is missing an email address — collect it for quote delivery.');
    if (!customer.phone) add('warn', 'Customer is missing a phone number — collect it for follow-up.');
  }

  // Service-code / service-type mismatch (soft)
  if (customer?.service_type && service?.status === 'active') {
    const st = (customer.service_type || '').toLowerCase();
    const nm = (service.name || '').toLowerCase();
    const looksMoving = st.includes('mov') || st === 'moving';
    if (looksMoving && !nm.includes('mov') && !service.code.startsWith('MOV')) {
      add('warn', `Lead service type is "${customer.service_type}" but the selected service is ${service.code}. Confirm this is the right service code.`);
    }
  }

  // Suggested intake questions from required inputs
  const reqs = service?.required_inputs || [];
  if (reqs.length) {
    add('question', `Suggested questions to ask the customer: ${reqs.map((r) => r.replace(/_/g, ' ')).join(', ')}.`);
  }

  if (!lineItems?.some((li) => (li.description || '').trim())) {
    add('info', 'Add at least one line item describing the work before saving the quote.');
  }

  return items;
};

const ASSIST_STYLES = {
  block: { cls: 'text-rose-700 bg-rose-50 border-rose-200', Icon: ShieldAlert },
  warn: { cls: 'text-amber-800 bg-amber-50 border-amber-200', Icon: Info },
  info: { cls: 'text-blue-800 bg-blue-50 border-blue-200', Icon: Sparkles },
  question: { cls: 'text-indigo-800 bg-indigo-50 border-indigo-200', Icon: MessageSquare },
};

// ── Internal cost model per authorized service (decision-support only; never customer-visible,
// never mutates BLA-STD-007). "undefinedModel" services do NOT fabricate a cost. ──────────────
const COST_MODEL = {
  'MOV-01': { kind: 'job', labor: true, vehicle: true, fuel: true, extras: true },
  'MOV-02': { kind: 'job', labor: true, vehicle: true, fuel: true, extras: true },
  'DSP-01': { kind: 'undefined' },
  'LSO-01': { kind: 'leaseon', blueLockShare: 0.16, driverShare: 0.84 },
};
const N = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));

// Deterministic, traceable internal cost + profitability estimate.
const computeInternalCost = ({ serviceCode, vehicleMode, cost, expenses, customerPrice, standardPrice }) => {
  const model = COST_MODEL[serviceCode];
  const missing = [];
  const breakdown = {};
  const assumptions = {};
  let total = 0;
  const add = (key, val) => { if (val != null) { breakdown[key] = Math.round(val * 100) / 100; total += val; } };

  const manualExpenses = (expenses || []).reduce((s, e) => s + (N(e.amount) || 0), 0);

  if (!model) {
    return { available: false, reason: 'Select an authorized service to estimate internal cost.' };
  }

  if (model.kind === 'undefined') {
    // DSP-01: no approved BlueLock internal cost model — do NOT fabricate carrier/driver costs.
    add('other_expenses', manualExpenses || null);
    return {
      available: true, model: 'undefined', service_code: serviceCode,
      notice: 'Internal cost model not yet defined for this service. Only manually-entered BlueLock administrative expenses are counted; carrier/driver operating costs are NOT BlueLock expenses.',
      expenses, breakdown, total_cost: Math.round(total * 100) / 100,
      completeness: manualExpenses > 0 ? 'partial' : 'missing', missing_inputs: [],
      customer_price: customerPrice, standard_price: standardPrice,
      gross_profit: customerPrice != null ? Math.round((customerPrice - total) * 100) / 100 : null,
      gross_margin: customerPrice > 0 ? Math.round(((customerPrice - total) / customerPrice) * 1000) / 10 : null,
      break_even: Math.round(total * 100) / 100,
      below_cost: customerPrice != null ? customerPrice < total : false,
      computed_at: new Date().toISOString(),
    };
  }

  if (model.kind === 'leaseon') {
    // LSO-01: reuse the approved 84% driver / 16% BlueLock economic structure. The driver share is
    // NOT a conventional job expense — present the economics accurately.
    const blShare = customerPrice != null ? customerPrice * model.blueLockShare : null;
    const driverShare = customerPrice != null ? customerPrice * model.driverShare : null;
    const adminCost = manualExpenses;
    return {
      available: true, model: 'leaseon', service_code: serviceCode,
      lease_on: { driver_share_pct: model.driverShare, bluelock_share_pct: model.blueLockShare, driver_share: driverShare, bluelock_share: blShare },
      notice: 'Lease-On economics use the approved 84% driver / 16% BlueLock split. The 84% driver share is settled through the existing settlement architecture and is not a job expense.',
      expenses, breakdown: { bluelock_admin_expenses: adminCost || 0 }, total_cost: adminCost,
      completeness: customerPrice != null ? 'complete' : 'missing', missing_inputs: customerPrice == null ? ['customer_price'] : [],
      customer_price: customerPrice, standard_price: standardPrice,
      gross_profit: blShare != null ? Math.round((blShare - adminCost) * 100) / 100 : null,
      gross_margin: blShare != null && customerPrice > 0 ? Math.round(((blShare - adminCost) / customerPrice) * 1000) / 10 : null,
      break_even: null,
      below_cost: blShare != null ? blShare < adminCost : false,
      computed_at: new Date().toISOString(),
    };
  }

  // Job-cost model (MOV-01 / MOV-02)
  const mileage = N(cost.mileage);
  const mpg = N(cost.mpg);
  const fuelPrice = N(cost.fuelPrice);
  assumptions.mileage = mileage; assumptions.mileage_type = 'manual';
  assumptions.mpg = mpg; assumptions.fuel_price = fuelPrice;

  // Fuel
  if (model.fuel) {
    if (mileage != null && mpg && fuelPrice != null) {
      const gallons = mileage / mpg;
      assumptions.gallons = Math.round(gallons * 100) / 100;
      add('fuel', gallons * fuelPrice);
    } else if (mileage != null || mpg != null || fuelPrice != null) {
      if (mileage == null) missing.push('mileage');
      if (!mpg) missing.push('mpg');
      if (fuelPrice == null) missing.push('fuel_price');
    }
  }
  // Vehicle / rental
  if (model.vehicle) {
    if (vehicleMode === 'rental') {
      const base = N(cost.rentalBase);
      const rate = N(cost.rentalMileageRate);
      const fees = N(cost.rentalFees) || 0;
      if (base != null) {
        const mileageCost = rate != null && mileage != null ? rate * mileage : 0;
        add('vehicle', base + mileageCost + fees);
      } else { missing.push('rental_base_cost'); }
    } else if (vehicleMode === 'company') {
      const perMile = N(cost.companyMileageRate);
      if (perMile != null && mileage != null) add('vehicle', perMile * mileage);
      // No approved per-mile company vehicle assumption is fabricated when absent.
    }
  }
  // Labor
  if (model.labor) {
    const workers = N(cost.workers);
    const hours = N(cost.laborHours);
    const rate = N(cost.laborRate);
    assumptions.labor_rate = rate;
    if (workers && hours && rate != null) add('labor', workers * hours * rate);
    else if (workers != null || hours != null || rate != null) {
      if (!workers) missing.push('workers'); if (!hours) missing.push('labor_hours'); if (rate == null) missing.push('labor_rate');
    }
  }
  // Extras
  if (model.extras) {
    add('equipment', N(cost.equipment));
    add('tolls', N(cost.tolls));
    add('parking', N(cost.parking));
    add('disposal', N(cost.disposal));
  }
  add('other_expenses', manualExpenses || null);
  add('contingency', N(cost.contingency));

  const anyEntered = Object.keys(breakdown).length > 0;
  const completeness = !anyEntered ? 'missing' : (missing.length ? 'partial' : 'complete');

  return {
    available: true, model: 'job', service_code: serviceCode, vehicle_mode: vehicleMode,
    assumptions, breakdown, expenses, total_cost: Math.round(total * 100) / 100,
    completeness, missing_inputs: missing,
    customer_price: customerPrice, standard_price: standardPrice,
    gross_profit: customerPrice != null ? Math.round((customerPrice - total) * 100) / 100 : null,
    gross_margin: customerPrice > 0 ? Math.round(((customerPrice - total) / customerPrice) * 1000) / 10 : null,
    break_even: Math.round(total * 100) / 100,
    below_cost: customerPrice != null ? customerPrice < total : false,
    computed_at: new Date().toISOString(),
  };
};

const CostInput = ({ label, value, onChange, testid }) => (
  <div className="flex flex-col">
    <label className="text-[10px] text-gray-500 mb-0.5">{label}</label>
    <input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-28 py-1.5 px-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
  </div>
);

const Metric = ({ label, value, testid, tone = 'slate' }) => {
  const tones = { slate: 'text-gray-900', green: 'text-green-700', rose: 'text-rose-700' };
  return (
    <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5">
      <p className="text-[10px] text-gray-500">{label}</p>
      <p className={`text-base font-bold ${tones[tone]}`} data-testid={testid}>{value}</p>
    </div>
  );
};

const QuoteBuilder = ({ intelPrefill = null, intelSource = null }) => {
  const [disabled, setDisabled] = useState(false);
  const [standard, setStandard] = useState(null);
  const [loadingStd, setLoadingStd] = useState(true);
  const [error, setError] = useState(null);

  // Customer selection
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadStandard = useCallback(async () => {
    setLoadingStd(true);
    try {
      const s = await pricingService.getStandard();
      setStandard(s); setDisabled(false);
    } catch (e) {
      if (e?.response?.status === 403) setDisabled(true);
      else setError('Failed to load the pricing standard.');
    } finally { setLoadingStd(false); }
  }, []);

  useEffect(() => { loadStandard(); }, [loadStandard]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setSearching(true); setError(null);
    try {
      const d = await leadService.listLeads({ q: q.trim(), limit: 15 });
      setResults(d.items || []);
    } catch (err) {
      if (err?.response?.status === 403) setDisabled(true);
      else setError('Search failed.');
    } finally { setSearching(false); }
  };

  const selectCustomer = async (lead) => {
    try {
      const full = await leadService.getLead(lead.id);
      setCustomer(full || lead);
    } catch { setCustomer(lead); }
    setResults([]); setQ('');
  };

  if (disabled) {
    return (
      <div className="max-w-3xl mx-auto text-center py-20" data-testid="quote-builder-disabled">
        <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">Quote Builder is not active</h2>
        <p className="text-gray-600 mt-2">Enable the <code className="text-sm bg-gray-100 px-1 rounded">quote_builder_enabled</code> feature flag to use the Quote Builder.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="quote-builder">
      <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
        <Link to="/admin/dashboard" className="hover:text-gray-700" data-testid="qb-breadcrumb-home">Admin Panel</Link>
        <ChevronRight className="w-4 h-4 mx-1" /><span className="text-gray-900 font-medium">Quote Builder</span>
      </nav>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Calculator className="w-7 h-7 text-blue-600" /> Quote Builder</h1>
          <p className="text-gray-600 mt-1">Internal, admin-only. Build governed BLA-STD-007 quotes for a customer or lead.</p>
        </div>
        <button onClick={loadStandard} data-testid="qb-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}

      {/* Step 1 — Customer / Lead selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="qb-customer-section">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> 1. Customer / Lead</h2>
          <button onClick={() => setShowNew((s) => !s)} data-testid="qb-new-customer-btn" className="text-sm text-blue-600 hover:underline">{showNew ? 'Cancel' : '+ New customer / lead'}</button>
        </div>

        {!customer && (
          <form onSubmit={runSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing customer by name / email / phone" data-testid="qb-customer-search" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <button type="submit" disabled={searching} data-testid="qb-customer-search-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search</button>
          </form>
        )}

        {showNew && !customer && <NewCustomer onCreated={(lead) => { setShowNew(false); setCustomer(lead); }} />}

        {results.length > 0 && !customer && (
          <div className="border border-gray-100 rounded-lg divide-y" data-testid="qb-customer-results">
            {results.map((l) => (
              <button key={l.id} type="button" onClick={() => selectCustomer(l)} data-testid={`qb-customer-result-${l.id}`} className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between">
                <span><span className="font-medium text-gray-900">{l.full_name}</span> <span className="text-xs text-gray-500">· {l.email}</span></span>
                <span className="text-xs text-gray-400 capitalize">{(l.service_type || '').replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        )}

        {customer && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start justify-between" data-testid="qb-selected-customer">
            <div className="text-sm">
              <p className="font-semibold text-gray-900">{customer.full_name}</p>
              <p className="text-gray-600">{customer.email}{customer.phone ? ` · ${customer.phone}` : ''}</p>
              <p className="text-gray-500 text-xs mt-0.5 capitalize">Service type: {(customer.service_type || 'general').replace('_', ' ')} · Status: {customer.status}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Link to={`/admin/customer-360?customer=${customer.id}`} target="_blank" rel="noopener noreferrer" data-testid="qb-open-360" className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"><ExternalLink className="w-3 h-3" /> Open Customer 360</Link>
              <button onClick={() => { setCustomer(null); setResults([]); }} data-testid="qb-change-customer" className="text-xs text-gray-500 hover:text-gray-800">Change customer</button>
            </div>
          </div>
        )}
      </div>

      {customer && standard && (
        <CustomerWorkspace customer={customer} standard={standard} />
      )}
      {customer && loadingStd && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>}

      {/* Operational Intelligence — always available (route/fit/cost/pricing). Not gated on a customer or partner submission. */}
      <QuoteOperationalIntelligence prefill={intelPrefill} sourceLabel={intelSource} />
    </div>
  );
};

const NewCustomer = ({ onCreated }) => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', service_type: 'general', message: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const SERVICE_TYPES = ['general', 'freight', 'white_glove', 'moving', 'driveaway', 'brokerage', 'courier', 'other'];

  const create = async (e) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const lead = await leadService.createLead({ ...form, source: 'manual_entry' }); onCreated(lead); }
    catch (e2) { setErr(e2?.response?.data?.detail || 'Create failed.'); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={create} className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3" data-testid="qb-new-customer-form">
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Full name *</label><input required value={form.full_name} onChange={upd('full_name')} data-testid="qb-nc-name" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Email *</label><input type="email" required value={form.email} onChange={upd('email')} data-testid="qb-nc-email" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Phone</label><input value={form.phone} onChange={upd('phone')} data-testid="qb-nc-phone" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Service type</label><select value={form.service_type} onChange={upd('service_type')} data-testid="qb-nc-service" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">{SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
      </div>
      {err && <p role="alert" className="text-xs text-red-600">{err}</p>}
      <button type="submit" disabled={saving} data-testid="qb-nc-submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Create & select</button>
    </form>
  );
};

const CustomerWorkspace = ({ customer, standard }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDraft, setEditingDraft] = useState(null); // quote object being edited
  const [c360, setC360] = useState(null);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try { const d = await quoteService.listQuotes({ lead_id: customer.id }); setQuotes(d.items || []); }
    catch { /* handled by flag gates elsewhere */ }
    finally { setLoading(false); }
  }, [customer.id]);

  const load360 = useCallback(async () => {
    try { setC360(await integrationService.getCustomer360(customer.id)); } catch { setC360(null); }
  }, [customer.id]);

  useEffect(() => { loadQuotes(); load360(); }, [loadQuotes, load360]);

  return (
    <div className="space-y-6">
      {/* Customer 360 mini panel */}
      {c360 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="qb-360-panel">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Layers className="w-5 h-5 text-blue-600" /> Customer 360 (reference)</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(c360.counts || {}).map(([k, v]) => (
              <span key={k} data-testid={`qb-360-count-${k}`} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 capitalize">{k}: {v}</span>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div data-testid="qb-360-documents"><p className="font-medium text-gray-700 flex items-center gap-1 mb-1"><FileText className="w-3.5 h-3.5" /> Documents</p>{(c360.chain?.documents || []).length ? c360.chain.documents.slice(0, 4).map((d, i) => <p key={i} className="text-gray-600 truncate">{d.title || d.name || d.category || d.id}</p>) : <p className="text-gray-400">None</p>}</div>
            <div data-testid="qb-360-jobs"><p className="font-medium text-gray-700 flex items-center gap-1 mb-1"><GitBranch className="w-3.5 h-3.5" /> Jobs</p>{(c360.chain?.jobs || []).length ? c360.chain.jobs.slice(0, 4).map((j, i) => <p key={i} className="text-gray-600 truncate">{(j.reference_number || j.load_number || j.id)} — {j.outcome === 'not_performed' ? 'not performed' : (j.status || 'unknown')}</p>) : <p className="text-gray-400">No jobs yet</p>}</div>
            <div data-testid="qb-360-timeline"><p className="font-medium text-gray-700 flex items-center gap-1 mb-1"><History className="w-3.5 h-3.5" /> Timeline</p>{(c360.operations_timeline || []).length ? c360.operations_timeline.slice(0, 4).map((t, i) => <p key={i} className="text-gray-600 truncate">{t.action || t.id}</p>) : <p className="text-gray-400">No events</p>}</div>
          </div>
        </div>
      )}

      {/* Existing quotes */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="qb-quotes-section">
        <h2 className="font-semibold text-gray-900">Quotes for {customer.full_name} {loading ? '' : `(${quotes.length})`}</h2>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
        ) : quotes.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="qb-quotes-empty">No quotes yet. Build one below.</p>
        ) : (
          <div className="space-y-2" data-testid="qb-quotes-list">
            {quotes.map((qt) => <ExistingQuoteRow key={qt.id} q={qt} onChanged={loadQuotes} onEdit={() => setEditingDraft(qt)} />)}
          </div>
        )}
      </div>

      {/* Builder / editor form */}
      <QuoteForm
        customer={customer}
        standard={standard}
        editingDraft={editingDraft}
        onDone={() => { setEditingDraft(null); loadQuotes(); load360(); }}
        onCancelEdit={() => setEditingDraft(null)}
      />
    </div>
  );
};

const ExistingQuoteRow = ({ q, onChanged, onEdit }) => {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showActuals, setShowActuals] = useState(false);
  const [act, setAct] = useState({ actual_cost: '', actual_revenue: '', notes: '' });
  const [savingAct, setSavingAct] = useState(false);
  const portalLink = q.public_token ? `${window.location.origin}/quote/${q.public_token}` : null;
  const isSentLike = ['sent', 'viewed', 'accepted', 'declined', 'expired', 'cancelled'].includes(q.status);

  const run = async (label, fn) => {
    setBusy(label); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      setErr(e?.response?.status === 403 ? 'Quote Workflow is not active (quote_workflow_enabled).' : (d || 'Action failed.'));
    } finally { setBusy(null); }
  };

  const openQuotePdf = () => {
    // Customer-safe whitelist ONLY — never expose internal cost/profit/margin/actuals/exceptions.
    const esc = (s) => String(s == null ? '' : s).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
    const items = Array.isArray(q.line_items) ? q.line_items : [];
    const itemRows = items.length
      ? items.map((it) => `<tr><td>${esc(it.description || it.label || it.name || 'Service')}</td><td style="text-align:right">${money(it.amount ?? it.total ?? it.price ?? '')}</td></tr>`).join('')
      : (q.scope_summary ? `<tr><td>${esc(q.scope_summary)}</td><td style="text-align:right">${money(q.subtotal ?? q.total)}</td></tr>` : '');
    const dt = q.created_at ? new Date(q.created_at).toLocaleDateString() : '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>BlueLock Quote ${esc(q.quote_number || q.id || '')}</title>
      <style>body{font-family:Georgia,'Times New Roman',serif;color:#0f172a;max-width:720px;margin:32px auto;padding:0 24px}
      .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e3a8a;padding-bottom:14px}
      .brand{font-size:24px;font-weight:700;color:#1e3a8a;letter-spacing:.5px}.sub{color:#475569;font-size:12px}
      h1{font-size:16px;text-transform:uppercase;letter-spacing:1px;color:#1e3a8a;margin:28px 0 6px}
      table{width:100%;border-collapse:collapse;margin-top:8px}td,th{padding:8px 6px;border-bottom:1px solid #e2e8f0;font-size:14px}
      .tot{text-align:right;margin-top:12px;font-size:14px}.tot .g{font-size:18px;font-weight:700;color:#1e3a8a}
      .meta{font-size:13px;color:#334155;margin-top:6px}.terms{font-size:11px;color:#64748b;margin-top:28px;border-top:1px solid #e2e8f0;padding-top:12px}
      @media print{button{display:none}}</style></head>
      <body>
        <div class="hdr"><div><div class="brand">BlueLock Freights</div><div class="sub">Professional Moving &amp; Freight Services</div></div>
          <div style="text-align:right"><div style="font-weight:700">QUOTE</div><div class="sub">${esc(q.quote_number || q.id || '')}</div><div class="sub">${esc(dt)}</div></div></div>
        <div class="meta"><strong>Service:</strong> ${esc((q.service_type || '').replace(/_/g, ' '))}</div>
        <h1>Quote Details</h1>
        <table><thead><tr><th style="text-align:left">Description</th><th style="text-align:right">Amount</th></tr></thead><tbody>${itemRows}</tbody></table>
        <div class="tot">Subtotal: ${money(q.subtotal ?? q.total)}<br/>Tax: ${money(q.tax ?? 0)}<br/><span class="g">Total: ${money(q.total)}</span></div>
        ${q.offer_note ? `<div class="meta" style="margin-top:16px">${esc(q.offer_note)}</div>` : ''}
        <div class="terms">This quote reflects agreed pricing for the services described above. Prices are valid as of the quote date. BlueLock Freights — thank you for your business.</div>
        <button onclick="window.print()" style="margin-top:24px;padding:10px 18px;background:#1e3a8a;color:#fff;border:0;border-radius:6px;cursor:pointer">Print / Save as PDF</button>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div data-testid={`qb-quote-row-${q.id}`} className="bg-gray-50 border border-gray-100 rounded-lg p-2.5 space-y-2">
      <div className="flex items-center justify-between text-xs flex-wrap gap-2">
        <span className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full font-medium capitalize ${QUOTE_STATUS_STYLES[q.status] || 'bg-gray-100'}`} data-testid={`qb-quote-status-${q.id}`}>{q.status}</span>
          {q.service_code && <span className="text-gray-500">{q.service_code}</span>}
          <span className="text-gray-600">{(q.line_items || []).length} item(s)</span>
          {q.revision_number > 1 && <span className="text-gray-400">rev {q.revision_number}</span>}
        </span>
        <span className="font-semibold text-gray-900">{money(q.total)}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {q.status === 'draft' && (
          <>
            <button type="button" onClick={onEdit} data-testid={`qb-quote-edit-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"><Edit3 className="w-3 h-3" /> Edit draft</button>
            <button type="button" onClick={() => run('send', () => quoteService.sendQuote(q.id))} disabled={busy} data-testid={`qb-quote-send-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">{busy === 'send' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send</button>
          </>
        )}
        {(q.status === 'sent' || q.status === 'viewed') && (
          <>
            <button type="button" onClick={() => run('accepted', () => quoteService.setQuoteStatus(q.id, { status: 'accepted' }))} disabled={busy} data-testid={`qb-quote-accept-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"><Check className="w-3 h-3" /> Accept</button>
            <button type="button" onClick={() => run('declined', () => quoteService.setQuoteStatus(q.id, { status: 'declined' }))} disabled={busy} data-testid={`qb-quote-decline-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"><XCircle className="w-3 h-3" /> Decline</button>
            <button type="button" onClick={() => run('expired', () => quoteService.setQuoteStatus(q.id, { status: 'expired' }))} disabled={busy} data-testid={`qb-quote-expire-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"><Clock className="w-3 h-3" /> Expire</button>
          </>
        )}
        {isSentLike && (
          <button type="button" onClick={() => run('revise', () => quoteService.reviseQuote(q.id))} disabled={busy} data-testid={`qb-quote-revise-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">{busy === 'revise' ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3" />} Revise</button>
        )}
        {q.status === 'accepted' && !q.load_id && (
          <button type="button" onClick={() => run('create-job', () => quoteService.createJobFromQuote(q.id))} disabled={busy} data-testid={`qb-quote-create-job-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">{busy === 'create-job' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Truck className="w-3 h-3" />} Create Job</button>
        )}
        {q.status === 'accepted' && q.load_id && (
          <span data-testid={`qb-quote-job-linked-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-50 text-slate-600 border border-slate-200 rounded"><Truck className="w-3 h-3" /> Job linked</span>
        )}
        {portalLink && (
          <a href={portalLink} target="_blank" rel="noopener noreferrer" data-testid={`qb-quote-portal-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"><ExternalLink className="w-3 h-3" /> Portal</a>
        )}
        {q.status !== 'draft' && (
          <button type="button" onClick={openQuotePdf} data-testid={`qb-quote-pdf-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-white"><FileText className="w-3 h-3" /> PDF</button>
        )}
        {(q.status_history || []).length > 0 && (
          <button type="button" onClick={() => setShowHistory((s) => !s)} data-testid={`qb-quote-history-toggle-${q.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900"><History className="w-3 h-3" /> History</button>
        )}
      </div>

      {(q.exceptions || []).length > 0 && (
        <div className="text-[11px] bg-white border border-amber-100 rounded px-2 py-1" data-testid={`qb-quote-exceptions-${q.id}`}>
          {q.exceptions.map((ex, i) => (
            <p key={i} className="text-amber-800">Exception: standard {money(ex.standard_price)} → {money(ex.adjusted_price)} · approved by {ex.approving_authority} — {ex.explanation}</p>
          ))}
        </div>
      )}

      {q.internal_cost_estimate && (
        <div className="text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`qb-quote-cost-${q.id}`}>
          <span className="font-semibold text-slate-600">Internal:</span>
          <span>cost {money(q.internal_cost_estimate.total_cost)}</span>
          <span>profit {q.internal_cost_estimate.gross_profit != null ? money(q.internal_cost_estimate.gross_profit) : '—'}</span>
          <span>margin {q.internal_cost_estimate.gross_margin != null ? `${q.internal_cost_estimate.gross_margin}%` : '—'}</span>
          {q.internal_cost_estimate.below_cost && <span className="text-rose-700 font-semibold">BELOW COST</span>}
          <span className="text-slate-400">({q.internal_cost_estimate.completeness})</span>
          {isSentLike && <button type="button" onClick={() => setShowActuals((s) => !s)} data-testid={`qb-actuals-toggle-${q.id}`} className="text-indigo-600 hover:underline">{q.actuals ? 'Estimate vs Actual' : '+ Record actuals'}</button>}
        </div>
      )}

      {showActuals && (
        <div className="text-[11px] bg-white border border-indigo-200 rounded p-2 space-y-2" data-testid={`qb-actuals-${q.id}`}>
          <p className="font-semibold text-indigo-800">Estimate vs Actual (INTERNAL)</p>
          {q.actuals && (
            <div className="grid grid-cols-3 gap-2" data-testid={`qb-variance-${q.id}`}>
              <div><p className="text-gray-400">Est cost</p><p className="font-medium">{money(q.internal_cost_estimate?.total_cost)}</p></div>
              <div><p className="text-gray-400">Actual cost</p><p className="font-medium">{q.actuals.actual_cost != null ? money(q.actuals.actual_cost) : '—'}</p></div>
              <div><p className="text-gray-400">Variance</p><p className={`font-medium ${(q.actuals.variance?.cost || 0) > 0 ? 'text-rose-600' : 'text-green-600'}`}>{q.actuals.variance?.cost != null ? money(q.actuals.variance.cost) : '—'}</p></div>
              <div><p className="text-gray-400">Est profit</p><p className="font-medium">{q.internal_cost_estimate?.gross_profit != null ? money(q.internal_cost_estimate.gross_profit) : '—'}</p></div>
              <div><p className="text-gray-400">Actual profit</p><p className="font-medium">{q.actuals.actual_profit != null ? money(q.actuals.actual_profit) : '—'}</p></div>
              <div><p className="text-gray-400">Margin Δ (pts)</p><p className="font-medium">{q.actuals.variance?.margin_points ?? '—'}</p></div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            <div><label className="block text-gray-500">Actual cost $</label><input type="number" value={act.actual_cost} onChange={(e) => setAct((a) => ({ ...a, actual_cost: e.target.value }))} data-testid={`qb-actual-cost-${q.id}`} className="w-24 py-1 px-1.5 border border-gray-300 rounded" /></div>
            <div><label className="block text-gray-500">Actual revenue $</label><input type="number" value={act.actual_revenue} onChange={(e) => setAct((a) => ({ ...a, actual_revenue: e.target.value }))} data-testid={`qb-actual-revenue-${q.id}`} className="w-24 py-1 px-1.5 border border-gray-300 rounded" placeholder={`${q.total}`} /></div>
            <button type="button" disabled={savingAct} data-testid={`qb-actual-save-${q.id}`} onClick={async () => { setSavingAct(true); setErr(null); try { await quoteService.recordActuals(q.id, { actual_cost: act.actual_cost === '' ? null : Number(act.actual_cost), actual_revenue: act.actual_revenue === '' ? null : Number(act.actual_revenue), notes: act.notes || null }); onChanged(); } catch (e2) { setErr(e2?.response?.data?.detail || 'Save failed.'); } finally { setSavingAct(false); } }} className="px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">{savingAct ? 'Saving…' : 'Save actuals'}</button>
          </div>
          <p className="text-[10px] text-gray-400">The frozen estimate is never overwritten — actuals are stored separately for variance analysis.</p>
        </div>
      )}

      {showHistory && (
        <div className="space-y-1 pt-1" data-testid={`qb-quote-history-${q.id}`}>
          {(q.status_history || []).map((h) => (
            <div key={h.id} className="text-[11px] text-gray-600 bg-white border border-gray-100 rounded px-2 py-1">
              <span className="capitalize font-medium text-gray-800">{h.from} → {h.to}</span>
              <span className="text-gray-400"> · {h.by} · {fmtDate(h.at)}</span>
              {h.note && <span className="text-gray-500"> — {h.note}</span>}
            </div>
          ))}
        </div>
      )}
      {err && <p role="alert" className="text-[11px] text-red-600">{err}</p>}
    </div>
  );
};

const emptyItem = () => ({ description: '', quantity: 1, unit_price: 0 });

const QuoteForm = ({ customer, standard, editingDraft, onDone, onCancelEdit }) => {
  const services = standard?.services || [];
  const [serviceCode, setServiceCode] = useState('');
  const [inputs, setInputs] = useState({ price: '', cost: '' });
  const [items, setItems] = useState([emptyItem()]);
  const [taxRate, setTaxRate] = useState(0);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [discountPct, setDiscountPct] = useState('');
  const [exception, setException] = useState({ enabled: false, explanation: '', approving_authority: '', standard_price: '', adjusted_price: '' });
  const [showCost, setShowCost] = useState(false);
  const [vehicleMode, setVehicleMode] = useState('none');
  const [cost, setCost] = useState({ mileage: '', fuelPrice: '', mpg: '', workers: '', laborHours: '', laborRate: '', rentalBase: '', rentalMileageRate: '', rentalFees: '', companyMileageRate: '', tolls: '', parking: '', disposal: '', equipment: '', contingency: '' });
  const [expenses, setExpenses] = useState([]);
  const [preview, setPreview] = useState(null);
  const [discountResult, setDiscountResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const service = useMemo(() => services.find((s) => s.code === serviceCode) || null, [services, serviceCode]);
  const isEditing = !!editingDraft;

  // Load an existing draft into the form for editing
  useEffect(() => {
    if (editingDraft) {
      setServiceCode(editingDraft.service_code || '');
      setInputs({ price: '', cost: '', ...(editingDraft.calculation_inputs || {}) });
      setItems((editingDraft.line_items || []).length ? editingDraft.line_items.map((li) => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price })) : [emptyItem()]);
      setTaxRate(editingDraft.tax_rate || 0);
      setValidUntil(editingDraft.valid_until || '');
      setNotes(editingDraft.notes || '');
      const ex = (editingDraft.exceptions || [])[0];
      if (ex) setException({ enabled: true, explanation: ex.explanation || '', approving_authority: ex.approving_authority || '', standard_price: ex.standard_price ?? '', adjusted_price: ex.adjusted_price ?? '' });
      const ice = editingDraft.internal_cost_estimate;
      if (ice) {
        setVehicleMode(ice.vehicle_mode || 'none');
        setExpenses(ice.expenses || []);
        const inp = ice.inputs || {};
        setCost((c) => ({ ...c, ...inp }));
        setShowCost(true);
      }
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [editingDraft]);

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0);
  const total = subtotal + tax;

  const setItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItems((a) => [...a, emptyItem()]);
  const removeItem = (idx) => setItems((a) => (a.length > 1 ? a.filter((_, i) => i !== idx) : a));

  const runPreview = async () => {
    if (!serviceCode) return;
    setPreviewing(true); setErr(null);
    try {
      const numericInputs = {};
      Object.entries(inputs).forEach(([k, v]) => { numericInputs[k] = v === '' ? undefined : (isNaN(Number(v)) ? v : Number(v)); });
      const p = await pricingService.quotePreview(serviceCode, numericInputs);
      setPreview(p);
      const price = Number(inputs.price), cost = Number(inputs.cost), dp = Number(discountPct) / 100;
      if (dp > 0 && price > 0) {
        setDiscountResult(await pricingService.discountCheck({ service_code: serviceCode, price, cost: cost || 0, discount_pct: dp }));
      } else setDiscountResult(null);
    } catch (e) { setErr(e?.response?.data?.detail || 'Preview failed.'); }
    finally { setPreviewing(false); }
  };

  const assist = useMemo(() => buildAssist({ customer, service, preview, discountResult, lineItems: items, exception }), [customer, service, preview, discountResult, items, exception]);
  const hardBlocks = assist.filter((a) => a.kind === 'block');

  const standardPrice = preview?.recommended_price ?? service?.minimum_charge ?? null;
  const estimate = useMemo(
    () => computeInternalCost({ serviceCode, vehicleMode, cost, expenses, customerPrice: total, standardPrice }),
    [serviceCode, vehicleMode, cost, expenses, total, standardPrice],
  );
  const marginHealth = classifyMarginHealth(estimate.gross_margin, estimate.below_cost);
  const confidence = estimateConfidence({ completeness: estimate.completeness, missingCount: (estimate.missing_inputs || []).length });

  const activeServices = services.filter((s) => s.status === 'active');
  const otherServices = services.filter((s) => s.status !== 'active');

  const buildPayload = () => {
    const numericInputs = {};
    Object.entries(inputs).forEach(([k, v]) => { if (v !== '' && v != null) numericInputs[k] = isNaN(Number(v)) ? v : Number(v); });
    const payload = {
      service_type: customer.service_type,
      service_code: serviceCode || null,
      pricing_method: service?.pricing_method || null,
      calculation_inputs: Object.keys(numericInputs).length ? numericInputs : null,
      line_items: items.filter((i) => (i.description || '').trim()).map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })),
      tax_rate: Number(taxRate) || 0,
      valid_until: validUntil || null,
      notes: notes || null,
    };
    if (Number(discountPct) > 0) payload.discounts = { routine_discount_pct: Number(discountPct) / 100 };
    if (exception.enabled) {
      payload.exceptions = [{
        exception_id: null,
        label: 'Governed pricing exception',
        service_code: serviceCode || null,
        standard_price: exception.standard_price === '' ? null : Number(exception.standard_price),
        adjusted_price: exception.adjusted_price === '' ? null : Number(exception.adjusted_price),
        explanation: exception.explanation,
        approving_authority: exception.approving_authority,
      }];
    }
    if (estimate?.available && estimate.completeness !== 'missing') {
      payload.internal_cost_estimate = {
        service_code: serviceCode,
        vehicle_mode: vehicleMode,
        inputs: { ...cost },
        expenses,
        assumptions: estimate.assumptions || null,
        breakdown: estimate.breakdown || null,
        total_cost: estimate.total_cost,
        completeness: estimate.completeness,
        missing_inputs: estimate.missing_inputs || [],
        customer_price: estimate.customer_price,
        standard_price: estimate.standard_price,
        gross_profit: estimate.gross_profit,
        gross_margin: estimate.gross_margin,
        break_even: estimate.break_even,
        below_cost: estimate.below_cost,
        lease_on: estimate.lease_on || null,
        model: estimate.model,
        margin_health: marginHealth.key,
        confidence: confidence.key,
        computed_at: estimate.computed_at,
      };
    }
    return payload;
  };

  const save = async () => {
    setErr(null);
    // Client-side governed guards (server enforces authoritatively too)
    if (!serviceCode) { setErr('Select a service before saving.'); return; }
    if (service && service.status !== 'active') { setErr(`${serviceCode} is not an active/quotable service and cannot be placed on a live quote.`); return; }
    if (exception.enabled && (!exception.explanation.trim() || !exception.approving_authority.trim())) {
      setErr('A pricing exception requires an explanation and an approving authority.'); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditing) await quoteService.updateQuote(editingDraft.id, payload);
      else await quoteService.createQuote({ lead_id: customer.id, ...payload });
      // reset
      setServiceCode(''); setInputs({ price: '', cost: '' }); setItems([emptyItem()]);
      setTaxRate(0); setValidUntil(''); setNotes(''); setDiscountPct('');
      setException({ enabled: false, explanation: '', approving_authority: '', standard_price: '', adjusted_price: '' });
      setVehicleMode('none'); setExpenses([]); setShowCost(false);
      setCost({ mileage: '', fuelPrice: '', mpg: '', workers: '', laborHours: '', laborRate: '', rentalBase: '', rentalMileageRate: '', rentalFees: '', companyMileageRate: '', tolls: '', parking: '', disposal: '', equipment: '', contingency: '' });
      setPreview(null); setDiscountResult(null);
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not save quote.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4" data-testid="qb-form">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Calculator className="w-5 h-5 text-blue-600" /> {isEditing ? `Edit draft (rev ${editingDraft.revision_number})` : '2. Build a quote'}</h2>
        {isEditing && <button onClick={onCancelEdit} data-testid="qb-cancel-edit" className="text-xs text-gray-500 hover:text-gray-800">Cancel edit</button>}
      </div>

      {/* Service selection */}
      <div data-testid="qb-service-select">
        <label className="block text-sm font-medium text-gray-700 mb-1">Service (only active services are quotable)</label>
        <select value={serviceCode} onChange={(e) => { setServiceCode(e.target.value); setPreview(null); }} data-testid="qb-service-dropdown" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
          <option value="">— Select a service —</option>
          <optgroup label="Active & quotable">
            {activeServices.map((s) => <option key={s.code} value={s.code}>{s.code} — {s.name}</option>)}
          </optgroup>
          <optgroup label="Not quotable (approval required)">
            {otherServices.map((s) => <option key={s.code} value={s.code} disabled>{s.code} — {s.name} ({s.status})</option>)}
          </optgroup>
        </select>
        {service && (
          <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="qb-service-detail">
            <span>Method: <b className="capitalize">{(service.pricing_method || '').replace(/_/g, ' ')}</b></span>
            {service.minimum_charge != null && <span>Minimum: <b>{money(service.minimum_charge)}</b></span>}
            {service.starting_rate != null && <span>Starting: <b>{money(service.starting_rate)}</b></span>}
            {service.status !== 'active' && <span className="text-rose-700 font-medium" data-testid="qb-service-nonquotable">Not quotable — {service.status}</span>}
          </div>
        )}
      </div>

      {/* Calculation inputs */}
      {service && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qb-inputs">
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Price ($)</label><input type="number" min="0" step="0.01" value={inputs.price} onChange={(e) => setInputs((f) => ({ ...f, price: e.target.value }))} data-testid="qb-input-price" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          <div><label className="block text-xs font-medium text-gray-700 mb-1">Cost ($)</label><input type="number" min="0" step="0.01" value={inputs.cost} onChange={(e) => setInputs((f) => ({ ...f, cost: e.target.value }))} data-testid="qb-input-cost" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          {(service.required_inputs || []).map((r) => (
            <div key={r}><label className="block text-xs font-medium text-gray-700 mb-1 capitalize">{r.replace(/_/g, ' ')}</label><input value={inputs[r] ?? ''} onChange={(e) => setInputs((f) => ({ ...f, [r]: e.target.value }))} data-testid={`qb-input-${r}`} className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
          ))}
          <div className="col-span-2 md:col-span-4">
            <button type="button" onClick={runPreview} disabled={previewing} data-testid="qb-preview-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50">{previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />} Preview pricing</button>
          </div>
        </div>
      )}

      {/* Pricing preview */}
      {preview && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1" data-testid="qb-preview-result">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>Method: <b className="capitalize">{(preview.pricing_method || service?.pricing_method || '').replace?.(/_/g, ' ') || preview.service_status}</b></span>
            {preview.minimum_charge_applied != null && <span className="text-amber-700">Min applied: <b>{money(preview.minimum_charge_applied)}</b></span>}
            {preview.contribution_margin != null && <span>Margin: <b>{(preview.contribution_margin * 100).toFixed(1)}%</b></span>}
            {preview.floor_price != null && <span>Floor: <b>{money(preview.floor_price)}</b></span>}
            {preview.recommended_price != null && <span>Recommended: <b>{money(preview.recommended_price)}</b></span>}
            <span>Confidence: <b className="capitalize">{preview.confidence}</b></span>
          </div>
          {(preview.flags || []).length > 0 && <div className="flex flex-wrap gap-1" data-testid="qb-preview-flags">{preview.flags.map((f) => <span key={f} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px]">{f}</span>)}</div>}
          {preview.approval_required && <p className="text-rose-700 text-xs font-medium" data-testid="qb-preview-approval">Founder approval / exception required.</p>}
        </div>
      )}

      {/* Line items */}
      <div data-testid="qb-line-items" className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Line items</label>
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
            <input value={it.description} onChange={(e) => setItem(idx, 'description', e.target.value)} placeholder="Description" data-testid={`qb-item-desc-${idx}`} className="col-span-6 py-1.5 px-2 text-sm border border-gray-300 rounded" />
            <input type="number" min="0" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} data-testid={`qb-item-qty-${idx}`} className="col-span-2 py-1.5 px-2 text-sm border border-gray-300 rounded" />
            <input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, 'unit_price', e.target.value)} data-testid={`qb-item-price-${idx}`} className="col-span-3 py-1.5 px-2 text-sm border border-gray-300 rounded" />
            <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-500" aria-label="Remove line item"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={addItem} data-testid="qb-add-item" className="text-xs text-blue-600 hover:underline">+ Add line item</button>
      </div>

      {/* Discount + exception + meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Discount %</label><input type="number" min="0" max="100" step="1" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} data-testid="qb-discount-pct" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Tax rate (0–1)</label><input type="number" min="0" max="1" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="qb-tax-rate" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-700 mb-1">Valid until</label><input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="qb-valid-until" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
      </div>
      {discountResult && (
        <p className="text-xs" data-testid="qb-discount-result">
          Discounted price: <b>{money(discountResult.discounted_price)}</b> · margin after: <b>{discountResult.margin_after != null ? `${(discountResult.margin_after * 100).toFixed(1)}%` : 'n/a'}</b>
          {discountResult.approval_required && <span className="text-rose-700 font-medium"> · approval required</span>}
          {discountResult.allowed === false && <span className="text-rose-700 font-medium"> · NOT allowed (breaks floor)</span>}
        </p>
      )}

      {/* Governed exception */}
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="qb-exception">
        <label className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <input type="checkbox" checked={exception.enabled} onChange={(e) => setException((x) => ({ ...x, enabled: e.target.checked }))} data-testid="qb-exception-toggle" /> Apply a governed pricing exception
        </label>
        {exception.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div><label className="block text-xs text-amber-900 mb-1">Standard price</label><input type="number" step="0.01" value={exception.standard_price} onChange={(e) => setException((x) => ({ ...x, standard_price: e.target.value }))} data-testid="qb-exception-standard" className="w-full py-1.5 px-2 text-sm border border-amber-300 rounded" /></div>
            <div><label className="block text-xs text-amber-900 mb-1">Adjusted (exception) price</label><input type="number" step="0.01" value={exception.adjusted_price} onChange={(e) => setException((x) => ({ ...x, adjusted_price: e.target.value }))} data-testid="qb-exception-adjusted" className="w-full py-1.5 px-2 text-sm border border-amber-300 rounded" /></div>
            <div className="md:col-span-2"><label className="block text-xs text-amber-900 mb-1">Explanation *</label><textarea rows={2} value={exception.explanation} onChange={(e) => setException((x) => ({ ...x, explanation: e.target.value }))} data-testid="qb-exception-explanation" className="w-full py-1.5 px-2 text-sm border border-amber-300 rounded" /></div>
            <div className="md:col-span-2"><label className="block text-xs text-amber-900 mb-1">Approving authority *</label><input value={exception.approving_authority} onChange={(e) => setException((x) => ({ ...x, approving_authority: e.target.value }))} data-testid="qb-exception-authority" placeholder="e.g. Founder" className="w-full py-1.5 px-2 text-sm border border-amber-300 rounded" /></div>
            <p className="md:col-span-2 text-[11px] text-amber-800">The standard price and the adjusted result are both preserved for audit. BLA-STD-007 is never changed.</p>
          </div>
        )}
      </div>

      {/* Notes */}
      <div><label className="block text-xs font-medium text-gray-700 mb-1">Notes</label><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="qb-notes" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>

      {/* Internal Cost & Profitability (INTERNAL ONLY) */}
      <div className="border-2 border-slate-300 rounded-lg overflow-hidden" data-testid="qb-cost">
        <button type="button" onClick={() => setShowCost((s) => !s)} data-testid="qb-cost-toggle" className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-800 text-white">
          <span className="flex items-center gap-2 text-sm font-semibold"><EyeOff className="w-4 h-4" /> Internal Cost & Profitability
            <span className="text-[10px] font-normal bg-white/15 rounded px-1.5 py-0.5">INTERNAL — NOT CUSTOMER VISIBLE</span>
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showCost ? 'rotate-180' : ''}`} />
        </button>
        {showCost && (
          <div className="p-3 space-y-4 bg-slate-50" data-testid="qb-cost-body">
            <div className="text-[11px] text-slate-600 bg-white border border-slate-200 rounded p-2" data-testid="qb-cost-source-note">
              <span className="font-semibold">Cost sources:</span> values you enter are <b>Confirmed</b>; calculated values are <b>System estimates</b> (assumptions shown). Live <b>market</b> rates (rental/fuel) &amp; route optimization are <b>not yet connected</b> — external providers are pending Founder approval, so enter costs manually for now. Estimates are never presented as confirmed or actual.
            </div>
            {!service ? (
              <p className="text-xs text-gray-500">Select a service to estimate BlueLock's internal cost.</p>
            ) : estimate.model === 'undefined' ? (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="qb-cost-undefined">{estimate.notice}</div>
            ) : estimate.model === 'leaseon' ? (
              <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-2 space-y-1" data-testid="qb-cost-leaseon">
                <p className="text-indigo-900">{estimate.notice}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
                  <span>Driver share (84%): <b>{money(estimate.lease_on?.driver_share)}</b></span>
                  <span>BlueLock share (16%): <b>{money(estimate.lease_on?.bluelock_share)}</b></span>
                </div>
              </div>
            ) : (
              <div className="space-y-3" data-testid="qb-cost-job">
                {/* Vehicle */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Vehicle</label>
                  <div className="flex flex-wrap gap-2 items-end">
                    <select value={vehicleMode} onChange={(e) => setVehicleMode(e.target.value)} data-testid="qb-cost-vehicle-mode" className="py-1.5 px-2 text-sm border border-gray-300 rounded">
                      <option value="none">No vehicle cost</option>
                      <option value="company">Company vehicle</option>
                      <option value="rental">Rental</option>
                    </select>
                    {vehicleMode === 'rental' && <>
                      <CostInput testid="qb-cost-rental-base" label="Rental base $" value={cost.rentalBase} onChange={(v) => setCost((c) => ({ ...c, rentalBase: v }))} />
                      <CostInput testid="qb-cost-rental-mileage-rate" label="Rental $/mi" value={cost.rentalMileageRate} onChange={(v) => setCost((c) => ({ ...c, rentalMileageRate: v }))} />
                      <CostInput testid="qb-cost-rental-fees" label="Rental fees $" value={cost.rentalFees} onChange={(v) => setCost((c) => ({ ...c, rentalFees: v }))} />
                    </>}
                    {vehicleMode === 'company' && <CostInput testid="qb-cost-company-permile" label="Company $/mi (approved only)" value={cost.companyMileageRate} onChange={(v) => setCost((c) => ({ ...c, companyMileageRate: v }))} />}
                  </div>
                </div>
                {/* Mileage + Fuel */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> Mileage (manual estimate) & Fuel</label>
                  <div className="flex flex-wrap gap-2 items-end">
                    <CostInput testid="qb-cost-mileage" label="Est. mileage (MANUAL)" value={cost.mileage} onChange={(v) => setCost((c) => ({ ...c, mileage: v }))} />
                    <CostInput testid="qb-cost-mpg" label="Est. MPG" value={cost.mpg} onChange={(v) => setCost((c) => ({ ...c, mpg: v }))} />
                    <CostInput testid="qb-cost-fuel-price" label="Fuel $/gal" value={cost.fuelPrice} onChange={(v) => setCost((c) => ({ ...c, fuelPrice: v }))} />
                    <div className="text-xs text-gray-600 pb-1.5">
                      Gallons: <b data-testid="qb-cost-gallons">{estimate.assumptions?.gallons ?? '—'}</b> · Fuel cost: <b data-testid="qb-cost-fuel-cost">{estimate.breakdown?.fuel != null ? money(estimate.breakdown.fuel) : '—'}</b>
                    </div>
                  </div>
                </div>
                {/* Labor */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Labor (internal — not the customer labor rate)</label>
                  <div className="flex flex-wrap gap-2 items-end">
                    <CostInput testid="qb-cost-workers" label="Workers" value={cost.workers} onChange={(v) => setCost((c) => ({ ...c, workers: v }))} />
                    <CostInput testid="qb-cost-labor-hours" label="Labor hours" value={cost.laborHours} onChange={(v) => setCost((c) => ({ ...c, laborHours: v }))} />
                    <CostInput testid="qb-cost-labor-rate" label="Internal $/worker/hr" value={cost.laborRate} onChange={(v) => setCost((c) => ({ ...c, laborRate: v }))} />
                    <div className="text-xs text-gray-600 pb-1.5">Labor cost: <b data-testid="qb-cost-labor-cost">{estimate.breakdown?.labor != null ? money(estimate.breakdown.labor) : '—'}</b></div>
                  </div>
                </div>
                {/* Extras */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Equipment, tolls, parking, disposal, contingency</label>
                  <div className="flex flex-wrap gap-2 items-end">
                    <CostInput testid="qb-cost-equipment" label="Equipment $" value={cost.equipment} onChange={(v) => setCost((c) => ({ ...c, equipment: v }))} />
                    <CostInput testid="qb-cost-tolls" label="Tolls $" value={cost.tolls} onChange={(v) => setCost((c) => ({ ...c, tolls: v }))} />
                    <CostInput testid="qb-cost-parking" label="Parking $" value={cost.parking} onChange={(v) => setCost((c) => ({ ...c, parking: v }))} />
                    <CostInput testid="qb-cost-disposal" label="Disposal $" value={cost.disposal} onChange={(v) => setCost((c) => ({ ...c, disposal: v }))} />
                    <CostInput testid="qb-cost-contingency" label="Contingency $" value={cost.contingency} onChange={(v) => setCost((c) => ({ ...c, contingency: v }))} />
                  </div>
                </div>
              </div>
            )}

            {/* Manual expenses (all cost-bearing models) */}
            {service && estimate.model !== 'leaseon' && (
              <div data-testid="qb-cost-expenses">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">Other job expenses</label>
                  <button type="button" onClick={() => setExpenses((x) => [...x, { label: '', amount: '', note: '' }])} data-testid="qb-cost-add-expense" className="text-xs text-blue-600 hover:underline">+ Add expense</button>
                </div>
                {expenses.map((ex, i) => (
                  <div key={i} className="flex gap-2 mb-1 items-center">
                    <input value={ex.label} onChange={(e) => setExpenses((x) => x.map((v, j) => j === i ? { ...v, label: e.target.value } : v))} placeholder="Label" data-testid={`qb-cost-expense-label-${i}`} className="flex-1 py-1.5 px-2 text-sm border border-gray-300 rounded" />
                    <input type="number" value={ex.amount} onChange={(e) => setExpenses((x) => x.map((v, j) => j === i ? { ...v, amount: e.target.value } : v))} placeholder="$" data-testid={`qb-cost-expense-amount-${i}`} className="w-24 py-1.5 px-2 text-sm border border-gray-300 rounded" />
                    <button type="button" onClick={() => setExpenses((x) => x.filter((_, j) => j !== i))} className="text-red-500" aria-label="Remove expense"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Profitability panel */}
            {service && (
              <div className="rounded-lg border border-slate-300 bg-white p-3" data-testid="qb-profitability">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Profitability (internal)</span>
                  <div className="flex items-center gap-1.5">
                    <span data-testid="qb-margin-health" className={`text-[10px] px-1.5 py-0.5 rounded border ${HEALTH_TONES[marginHealth.tone]}`}>{marginHealth.label}</span>
                    <span data-testid="qb-cost-confidence" className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{confidence.label}</span>
                    <span data-testid="qb-cost-completeness" className={`text-[10px] px-1.5 py-0.5 rounded ${estimate.completeness === 'complete' ? 'bg-green-100 text-green-700' : estimate.completeness === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                      {estimate.completeness === 'complete' ? 'Complete estimate' : estimate.completeness === 'partial' ? 'Partial estimate' : 'Missing cost inputs'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <Metric label="Customer quote" testid="qb-prof-customer" value={money(estimate.customer_price)} />
                  <Metric label="Estimated BlueLock cost" testid="qb-prof-cost" value={money(estimate.total_cost)} />
                  <Metric label="Estimated gross profit" testid="qb-prof-profit" value={estimate.gross_profit != null ? money(estimate.gross_profit) : '—'} tone={estimate.gross_profit != null && estimate.gross_profit < 0 ? 'rose' : 'green'} />
                  <Metric label="Estimated gross margin" testid="qb-prof-margin" value={estimate.gross_margin != null ? `${estimate.gross_margin}%` : '—'} tone={estimate.below_cost ? 'rose' : 'slate'} />
                  <Metric label="Break-even price" testid="qb-prof-breakeven" value={estimate.break_even != null ? money(estimate.break_even) : 'n/a'} />
                  <Metric label="Standard / min price" testid="qb-prof-standard" value={estimate.standard_price != null ? money(estimate.standard_price) : '—'} />
                </div>
                {estimate.standard_price != null && estimate.customer_price != null && (
                  <p className="text-[11px] text-gray-500 mt-2" data-testid="qb-prof-diff">Difference from governed price: <b>{money(estimate.customer_price - estimate.standard_price)}</b></p>
                )}
                {estimate.below_cost && (
                  <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="qb-prof-below-cost">
                    <TrendingDown className="w-4 h-4" /> QUOTE IS BELOW ESTIMATED BLUELOCK COST — requires authorized review before sending.
                  </div>
                )}
                {estimate.completeness === 'partial' && (estimate.missing_inputs || []).length > 0 && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="qb-prof-missing">Partial estimate — missing: {estimate.missing_inputs.join(', ')}.</p>
                )}
                <p className="text-[10px] text-gray-400 mt-2">Margin thresholds are not pre-set (no governed thresholds approved). Only the objective below-cost warning is enforced. Recommend Founder approve HEALTHY/LOW thresholds.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI-assist */}
      <div className="border border-indigo-200 rounded-lg" data-testid="qb-assist">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-indigo-100 bg-indigo-50 rounded-t-lg">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-semibold text-indigo-900">AI Assist</span>
          <span className="text-[11px] text-indigo-700">— internal, deterministic. Suggestions require human review; nothing is applied automatically.</span>
        </div>
        <div className="p-3 space-y-1.5">
          {assist.length === 0 && <p className="text-xs text-gray-500">No suggestions.</p>}
          {assist.map((a, i) => {
            const st = ASSIST_STYLES[a.kind] || ASSIST_STYLES.info;
            const Icon = st.Icon;
            return (
              <div key={i} data-testid={`qb-assist-${a.kind}-${i}`} className={`flex items-start gap-2 text-xs border rounded px-2 py-1.5 ${st.cls}`}>
                <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><span className="font-medium">Suggestion:</span> {a.text}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Totals + save */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-gray-200">
        <div className="text-sm text-gray-700 flex gap-4" data-testid="qb-totals">
          <span>Subtotal <b>{money(subtotal)}</b></span><span>Tax <b>{money(tax)}</b></span><span>Total <b className="text-gray-900">{money(total)}</b></span>
        </div>
        <button type="button" onClick={save} disabled={saving || hardBlocks.length > 0} title={hardBlocks.length ? 'Resolve blocking suggestions first' : ''} data-testid="qb-save-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEditing ? 'Save changes' : 'Save draft quote'}</button>
      </div>
      {hardBlocks.length > 0 && <p className="text-xs text-rose-700" data-testid="qb-save-blocked">Resolve the blocking suggestion(s) above before saving.</p>}
      {err && <p role="alert" className="text-sm text-red-600" data-testid="qb-form-error">{err}</p>}
    </div>
  );
};

export default QuoteBuilder;
